/** XeAgents.ai**/
import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CalendarDays, CheckCircle2, XCircle, Loader2, AlertCircle, ArrowRight, Building2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { formatDate } from "@/lib/date-format";
import { withBase } from "@/lib/base-path";

interface InviteValidation {
  valid: boolean;
  email: string;
  name?: string;
  invitedBy?: string;
  organizationName?: string;
  expiresAt?: string;
  error?: string;
  domainApproved?: boolean;
  domainProvider?: string;
  domainAdminEmail?: string;
}

export default function CalendarConnectPage() {
  const { toast } = useToast();
  const [token, setToken] = useState<string | null>(null);

  // Extract token from URL on mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const tokenParam = urlParams.get('token');
    setToken(tokenParam);

    // Handle OAuth callback parameters
    const connected = urlParams.get('success');
    const error = urlParams.get('error');

    if (connected === 'true') {
      toast({
        title: "Calendar Connected",
        description: "Your calendar has been connected successfully! You can close this page.",
      });
    } else if (error) {
      toast({
        title: "Connection Failed",
        description: decodeURIComponent(error),
        variant: "destructive",
      });
    }
  }, [toast]);

  // Validate the invite token
  const { data: validation, isLoading, isError } = useQuery<InviteValidation>({
    queryKey: ["/api/flow-automation/calendar-team-members/validate-invite", token],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/flow-automation/calendar-team-members/validate-invite/${token}`);
      return res.json();
    },
    enabled: !!token,
    retry: false,
  });

  // State for loading during OAuth redirect
  const [isConnecting, setIsConnecting] = useState<'google' | 'microsoft' | 'domain' | null>(null);
  const [domainConnected, setDomainConnected] = useState(false);

  // Auto-connect via domain approval (no OAuth needed)
  const domainConnectMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", `/api/flow-automation/calendar-team-members/connect-via-domain`, {
        inviteToken: token,
      });
      return res.json();
    },
    onSuccess: () => {
      setDomainConnected(true);
      toast({
        title: "Calendar Connected",
        description: "You've been connected using your organization's approved calendar. You can close this page.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Connection Failed",
        description: error.message || "Failed to connect via domain approval",
        variant: "destructive",
      });
      setIsConnecting(null);
    },
  });

  const connectViaDomain = () => {
    setIsConnecting('domain');
    domainConnectMutation.mutate();
  };

  // Connect calendar via OAuth - redirect to OAuth start endpoint
  const connectGoogle = () => {
    setIsConnecting('google');
    window.location.href = withBase(`/api/flow-automation/calendar-team-members/oauth/start/google?inviteToken=${token}`);
  };

  const connectMicrosoft = () => {
    setIsConnecting('microsoft');
    window.location.href = withBase(`/api/flow-automation/calendar-team-members/oauth/start/microsoft?inviteToken=${token}`);
  };

  // No token provided
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle>Invalid Link</CardTitle>
            <CardDescription>
              No invitation token was provided. Please use the link from your email invitation.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Validating invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error or invalid token
  if (isError || !validation?.valid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <XCircle className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <CardTitle>Invalid or Expired Invitation</CardTitle>
            <CardDescription>
              {validation?.error || "This invitation link is no longer valid. Please contact your administrator for a new invitation."}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Check if connection was successful
  const urlParams = new URLSearchParams(window.location.search);
  const isConnected = urlParams.get('success') === 'true' || domainConnected;

  if (isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <CheckCircle2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle>Calendar Connected!</CardTitle>
            <CardDescription>
              {domainConnected
                ? "You've been connected using your organization's approved calendar."
                : "Your calendar has been connected successfully."
              } You can now close this page.
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground">
              AI agents can now schedule meetings using your calendar availability.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Domain is approved - show simplified connection flow
  if (validation?.domainApproved) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-green-600 dark:text-green-400" />
            </div>
            <CardTitle>Organization Calendar Available</CardTitle>
            <CardDescription>
              Your organization's calendar is already connected. You can use it without additional setup.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Domain approval info */}
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Your Email:</span>
                <span className="text-sm font-medium">{validation.email}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Organization Calendar:</span>
                <Badge variant="outline" className="text-xs bg-green-100 dark:bg-green-900/30">
                  {validation.domainProvider === 'google' ? 'Google' : 'Microsoft'} - {validation.domainAdminEmail}
                </Badge>
              </div>
              {validation.invitedBy && (
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Invited by:</span>
                  <span className="text-sm font-medium">{validation.invitedBy}</span>
                </div>
              )}
            </div>

            {/* Info box */}
            <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
                <p className="text-xs text-blue-700 dark:text-blue-300">
                  Your domain is pre-approved. Click below to confirm and start using the organization's calendar for meeting scheduling.
                </p>
              </div>
            </div>

            {/* Confirm button */}
            <Button
              onClick={connectViaDomain}
              disabled={isConnecting !== null}
              className="w-full"
            >
              {isConnecting === 'domain' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="h-4 w-4 mr-2" />
              )}
              Confirm & Connect
            </Button>

            {/* Alternative: connect own calendar */}
            <div className="pt-4 border-t">
              <p className="text-xs text-muted-foreground text-center mb-3">
                Or connect your own personal calendar instead:
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={connectGoogle}
                  disabled={isConnecting !== null}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  {isConnecting === 'google' ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <svg className="h-3 w-3 mr-1" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  )}
                  Google
                </Button>
                <Button
                  onClick={connectMicrosoft}
                  disabled={isConnecting !== null}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                >
                  {isConnecting === 'microsoft' ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <svg className="h-3 w-3 mr-1" viewBox="0 0 24 24">
                      <path fill="#F25022" d="M11.4 24H0V12.6h11.4V24z"/>
                      <path fill="#00A4EF" d="M24 24H12.6V12.6H24V24z"/>
                      <path fill="#7FBA00" d="M11.4 11.4H0V0h11.4v11.4z"/>
                      <path fill="#FFB900" d="M24 11.4H12.6V0H24v11.4z"/>
                    </svg>
                  )}
                  Microsoft
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Valid invitation - show connection options
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-950 dark:to-slate-900 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
            <CalendarDays className="h-6 w-6 text-primary" />
          </div>
          <CardTitle>Connect Your Calendar</CardTitle>
          <CardDescription>
            You've been invited to connect your calendar for meeting scheduling.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Invitation details */}
          <div className="p-4 rounded-lg bg-muted/50 border space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Email:</span>
              <span className="text-sm font-medium">{validation.email}</span>
            </div>
            {validation.name && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Name:</span>
                <span className="text-sm font-medium">{validation.name}</span>
              </div>
            )}
            {validation.organizationName && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Organization:</span>
                <span className="text-sm font-medium">{validation.organizationName}</span>
              </div>
            )}
            {validation.expiresAt && (
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">Expires:</span>
                <Badge variant="outline" className="text-xs">
                  {formatDate(validation.expiresAt, 'MMM d, yyyy')}
                </Badge>
              </div>
            )}
          </div>

          {/* Info box */}
          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400 mt-0.5" />
              <p className="text-xs text-blue-700 dark:text-blue-300">
                After connecting, AI voice agents will be able to check your calendar availability and schedule meetings on your behalf.
              </p>
            </div>
          </div>

          {/* Connection buttons */}
          <div className="space-y-3">
            <Button
              onClick={connectGoogle}
              disabled={isConnecting !== null}
              className="w-full"
              variant="outline"
            >
              {isConnecting === 'google' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              Connect with Google
              <ArrowRight className="h-4 w-4 ml-auto" />
            </Button>

            <Button
              onClick={connectMicrosoft}
              disabled={isConnecting !== null}
              className="w-full"
              variant="outline"
            >
              {isConnecting === 'microsoft' ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                  <path fill="#F25022" d="M11.4 24H0V12.6h11.4V24z"/>
                  <path fill="#00A4EF" d="M24 24H12.6V12.6H24V24z"/>
                  <path fill="#7FBA00" d="M11.4 11.4H0V0h11.4v11.4z"/>
                  <path fill="#FFB900" d="M24 11.4H12.6V0H24v11.4z"/>
                </svg>
              )}
              Connect with Microsoft
              <ArrowRight className="h-4 w-4 ml-auto" />
            </Button>
          </div>

          {/* What happens next */}
          <div className="pt-4 border-t">
            <h4 className="text-sm font-medium mb-2">What happens next?</h4>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
                <span>You'll be redirected to sign in with your calendar provider</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
                <span>Grant permission to read your calendar availability</span>
              </li>
              <li className="flex items-center gap-2">
                <CheckCircle2 className="h-3 w-3 text-green-500 shrink-0" />
                <span>AI agents can then schedule meetings on your calendar</span>
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
